/** Chrome's install event, which TypeScript's DOM lib doesn't describe. */
type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

export type InstallPlatform = 'installed' | 'in_app_browser' | 'ios' | 'android' | 'other';

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

// Chrome fires this once, early, often before the install screen mounts: keep it for later.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    listeners.forEach((notify) => notify());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((notify) => notify());
  });
}

export const canPromptInstall = (): boolean => deferred !== null;

export const onInstallChange = (notify: () => void): (() => void) => {
  listeners.add(notify);
  return () => listeners.delete(notify);
};

/** Shows Chrome's own "Install app" dialog; true when the customer accepted. */
export const promptInstall = async (): Promise<boolean> => {
  if (!deferred) return false;
  const event = deferred;
  deferred = null;
  await event.prompt();
  return (await event.userChoice).outcome === 'accepted';
};

/** Zalo and Facebook open scanned links in their own browser, which cannot install apps. */
const IN_APP_BROWSER = /Zalo|FBAN|FBAV|FB_IAB|Instagram|Line\/|TikTok/i;

export const installPlatform = (): InstallPlatform => {
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (standalone) return 'installed';
  const ua = navigator.userAgent;
  if (IN_APP_BROWSER.test(ua)) return 'in_app_browser';
  // iPadOS reports itself as a Mac; touch points give it away
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
};
